import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import TableContainer from "Components/Common/TableContainer";
import CustomButton from "Components/Custom/CustomButton";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import Icon, { IconName } from "Components/Icons/Icon";
import CustomShareModal from "Components/Modals/CustomShareModal";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import {
  getPaymentDetails,
  regeneratePaymentLink,
  sharePayment,
} from "slices/thunks";
import { clearPaymentDetails } from "slices/payments/reducer";
import { ErrorHandler } from "utils/toast";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";

// ─── Detail item component ────────────────────────────────────────────────────

interface DetailItemProps {
  icon: IconName;
  label: string;
  value: string;
}

const DetailItem = ({ icon, label, value }: DetailItemProps) => (
  <div className="d-flex flex-column gap-1" style={{ minWidth: 0 }}>
    <p className="text-grey89 fs-14 fw-normal font-primary mb-0 text-uppercase">
      {label}
    </p>
    <div
      className="d-flex align-items-center gap-2 mt-2"
      style={{ minWidth: 0 }}
    >
      <Icon name={icon} style={{ flexShrink: 0 }} />
      <p
        className="text-black2D fs-24 fw-semibold font-primary mb-0"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
        title={value}
      >
        {value}
      </p>
    </div>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const ViewPayment = () => {
  document.title = "View Payment | EaziQuote";

  const dispatch = useAppDispatch();
  const location = useLocation();
  const id = location.state?.id as number | undefined;

  const {
    paymentDetails: payment,
    status,
    generatedLinkDetails,
  } = useAppSelector((state) => state.payment);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleShare = async () => {
    if (!id) return;
    try {
      setShareModalOpen(true);
      await dispatch(regeneratePaymentLink({ id })).unwrap();
    } catch (error) {
      ErrorHandler(error);
      setShareModalOpen(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    dispatch(getPaymentDetails({ id }));
    return () => {
      dispatch(clearPaymentDetails());
    };
  }, [dispatch, id]);

  const isLoading = status.fetchDetails === "loading";
  const allocations: any[] = (payment?.allocations as any[]) ?? [];

  const allocationColumns = useMemo(
    () => [
      {
        header: "INVOICE",
        accessorKey: "reference",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() || "-"}
          </span>
        ),
      },
      {
        header: "DATE",
        accessorKey: "due_date",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cell.getValue())}
          </span>
        ),
      },
      {
        header: "AMOUNT",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
    ],
    [],
  );

  // Guard: must arrive via navigation with a valid id
  if (!id) {
    return <Navigate to="/payments" replace />;
  }

  return (
    <>
      <div className="page-content">
        <FullScreenLoader
          isLoading={isLoading}
          message="Loading payment details..."
        />
        <Container fluid>
          <div className="gap-3 d-flex flex-column">
            {/* ── Page Header ─────────────────────────────────────────── */}
            <div className="d-flex align-items-center justify-content-between">
              <p className="text-black2D fs-24 fw-bold font-primary m-0">
                Payment ID: {payment?.reference ?? "-"}
              </p>

              {/* Share button — only for pending/failed stripe payments */}
              {payment &&
                (payment.status === "pending" ||
                  payment.status === "failed") && (
                  <CustomButton
                    text="Share"
                    onClick={handleShare}
                    className="bg-primary py-2 fs-14 fw-medium font-primary"
                    leftIcon={<Icon name="Share" />}
                  />
                )}
            </div>

            {/* ── Payment Details Card ─────────────────────────────────── */}
            <Card className="br-10 bg-white">
              <CardBody className="p-0">
                <div className="d-flex flex-wrap align-items-stretch gap-0">
                  {[
                    {
                      icon: "PrimaryUser" as IconName,
                      label: "Client",
                      value: payment?.client_name ?? "-",
                    },
                    {
                      icon: "GreenEuro" as IconName,
                      label: "Amount",
                      value: payment ? `£${payment?.amount ?? 0}` : "-",
                    },
                    {
                      icon: "BlueCalendar" as IconName,
                      label: "Date",
                      value: payment?.date ?? "-",
                    },
                    {
                      icon: "GrayReset" as IconName,
                      label: "Type",
                      value: payment?.type ?? "-",
                    },
                    {
                      icon: "OrangeWallet" as IconName,
                      label: "Method",
                      value: payment?.method ?? "-",
                    },
                    {
                      icon: "CircularGreenMark" as IconName,
                      label: "Credit Remaining",
                      value: payment
                        ? `£${payment?.credit_remaining ?? 0}`
                        : "-",
                    },
                  ].map((item, index, arr) => (
                    <div
                      key={item.label}
                      className="d-flex align-items-center"
                      style={{ flex: "1 1 0", minWidth: 0 }}
                    >
                      <div className="px-4 py-4 w-100" style={{ minWidth: 0 }}>
                        <DetailItem
                          icon={item.icon}
                          label={item.label}
                          value={item.value}
                        />
                      </div>
                      {index < arr.length - 1 && (
                        <div
                          style={{
                            width: 1,
                            alignSelf: "stretch",
                            background: "#E5E7EB",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>

            {/* ── Allocation Table ─────────────────────────────────────── */}
            <Card className="br-10 bg-white">
              <CardBody>
                <p className="text-black2D fs-16 fw-bold font-primary">
                  Allocation
                </p>
                <div className="divider mb-4 pb-3 mx-n5" />
                <TableContainer
                  columns={allocationColumns}
                  data={allocations}
                  isGlobalFilter={false}
                  // isInitialLoading={isLoading}
                  divClass="table-responsive table-card mb-0 bg-white"
                  tableClass="align-middle table-nowrap table-borderless"
                  thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5"
                  theadClass="table-light"
                  onSearch={() => {}}
                  onPageChange={() => {}}
                />
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>
      {payment &&
        (payment.status === "pending" || payment.status === "failed") && (
          <CustomShareModal
            isOpen={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            title="Share Payment Link"
            shareUrl={generatedLinkDetails?.stripe_payment_link_url ?? ""}
            subject="Payment Link"
            isLoading={status.regenerateLink === "loading"}
            recipientEmail={generatedLinkDetails?.client_email ?? ""}
            message={
              generatedLinkDetails?.client_name
                ? `Hi ${generatedLinkDetails.client_name}, here is your payment link:\n\n${generatedLinkDetails.stripe_payment_link_url}\n\nThank you.`
                : `Here is your payment link:\n\n${
                    generatedLinkDetails?.stripe_payment_link_url ?? ""
                  }\n\nThank you.`
            }
            onSendEmail={() =>
              generatedLinkDetails?.id &&
              dispatch(sharePayment({ id: generatedLinkDetails.id }))
            }
            isSending={status.share === "loading"}
          />
        )}
    </>
  );
};

export default ViewPayment;
